/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './ChatBot';
import { toOptions } from 'componentregistry/utils/ManagerUtils';

const ChatBotManager: ComponentManagerFn<'ChatBot'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'ChatBot'>(platform, 'ChatBot'),
    type: 'ChatBot',
    name: 'ChatBot',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      botName: {
        label: 'ChatBot Name',
        type: 'string',
        helpText: 'Specify a name for the ChatBot.',
      },
      welcomeMessage: {
        label: 'Welcome Message',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Specify the welcome message.',
      },
      askAPI: {
        label: 'ChatBot API',
        type: 'select',
        required: true,
        options: toOptions(['Python', 'Rust']),
        category: 'Flex',
      },
      wfId: {
        label: 'Workflow Id',
        type: 'string',
        helpText: 'Specify workflow for ChatBot.',
      },
      confidenceThreshold: {
        label: 'Threshold Confidence',
        type: 'number',
        helpText: 'Specify Threshold Confidence for ChatBot.',
      },
    },
    // props: {
    //   dsalias: {
    //     category: 'Data',
    //     label: 'DataSource Alias',
    //     required: true,
    //     type: 'DataSourceAliasChooser',
    //     allowedTypes: [
    //       'CloudDataStore',
    //       'DataStore',
    //       'LocalDataStore',
    //       'ReadOnlyDataStore',
    //     ],
    //   },
    // },
    // getDataSourceAliasProperty: (aliasType: AliasType) =>
    //   aliasType === 'LOV'
    //     ? { aliasProp: 'dsalias', embeddedPageIdProp: 'embeddedPageId' } // TODO: Add embeddedPageId prop
    //     : undefined,
    // isDataSourceAliasUsed: (item, alias) => item.props.dsalias === alias,
    // getDisplayText: (item) => item.props.icon?.iconName ?? 'Choose icon!',
    category: 'Forms',
    icon: { iconName: 'message-bot' },
    description: `**Overview**<br/>
The ChatBot component is a conversational interface that allows users to interact with an AI or workflow-driven backend.
Instead of navigating complex forms or menus, users can ask questions or perform actions using natural language (e.g., "Check status of Order #123" or "Reset my password"). It is designed to be a "Helper" or "Assistant" overlay, typically placed in a corner or a dedicated support panel.

**1. Identity & Greeting**<br/>
You can customize the bot's persona to match your application's tone.
* **ChatBot Name:** The display name shown in the chat header (e.g., "Support Assistant" or "Ops Bot").
* **Welcome Message:** The initial greeting sent to the user as soon as the chat window opens.
* **Expression Support:** You can make this dynamic (e.g., \`"Hello " + $.user.firstName + ", how can I help?"\`).

**2. Intelligence Connection (The Brain)**<br/>
The bot needs a backend engine to process user input and return answers.
* **ChatBot API:** Selects the backend technology stack used for processing (Options: Python or Rust). This determines the expected API contract.
* **Workflow Id:** The ID of the specific workflow that powers this bot's logic. When a user types a message, this workflow is triggered to process the intent and return a response.
* **Threshold Confidence:** A numeric value (0.0 to 1.0) defining the "Safety Level." If the bot's confidence in an answer falls below this threshold, it can be configured to fallback to a "Human Handoff" or "I don't understand" message.

**3. Interactivity**<br/>
* **TriggerOnClick:** You can add this child component to execute client-side logic when the bot is opened or clicked (e.g., "Log 'Help Opened' to Analytics").`,
  };
};

export default ChatBotManager;
